import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import {
  createLeadFinancial,
  CreateLeadFinancialApiPayload,
} from '@/actions/leads/createLeadFinancial';
import { GetLeadFinancialApiResponse } from '@/actions/leads/getLeadFinancial';
import { ApiResponse } from '@/types/response.type';

export interface UseCreateLeadFinancialReturn {
  mutate: (
    candidateId: string,
    payload: Omit<CreateLeadFinancialApiPayload, 'zee_id'>
  ) => Promise<ApiResponse<GetLeadFinancialApiResponse> | undefined>;
  result: ApiResponse<GetLeadFinancialApiResponse> | undefined;
  error: Error | null;
  isError: boolean;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useCreateLeadFinancial = (): UseCreateLeadFinancialReturn => {
  const queryClient = useQueryClient();

  const mutation: UseMutationResult<
    ApiResponse<GetLeadFinancialApiResponse> | undefined,
    Error,
    {
      candidateId: string;
      payload: Omit<CreateLeadFinancialApiPayload, 'zee_id'>;
    }
  > = useMutation({
    mutationFn: ({ candidateId, payload }) =>
      createLeadFinancial(candidateId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['zee-candidate-financial', variables.candidateId],
      });
    },
  });

  return {
    mutate: (candidateId, payload) =>
      mutation.mutateAsync({ candidateId, payload }),
    result: mutation.data,
    error: mutation.error,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    isLoading: mutation.isPending,
  };
};
